import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar } from '@/components/brand/Brand';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Feedback';
import { Pill } from '@/components/ui/Pill';
import { Stepper } from '@/components/ui/Stepper';
import { useToast } from '@/components/ui/Toast';
import { groupRoundsApi } from '@/lib/api/endpoints';
import { qk, useGroupRound } from '@/lib/api/queries';
import type {
  GroupParticipant,
  GroupRound,
  TeamId,
} from '@/lib/api/types';
import { cx } from '@/lib/cx';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { fmtDecimal, fmtToPar } from '@/lib/format/number';
import {
  GroupScoreGrid,
  type GridHoleEntry,
  type GridTeamEntry,
} from './GroupScoreGrid';
import { holeOf, scorableParticipants, strokesReceivedOnHole } from './groupMath';
import { t } from '@/lib/i18n';
import styles from './Record.module.css';

/**
 * Scorecard em grupo — "cada um por si, quatro linhas independentes" (canvas 1s).
 *
 * A decisão que organiza a tela: **as quatro linhas cabem sem rolagem**, e é isso que permite
 * marcar o buraco inteiro de uma vez, que é como um cartão de golfe funciona na vida real. A
 * alternativa (um jogador por vez, em abas) custaria quatro trocas de contexto por buraco.
 *
 * O stepper é de 44 px em **todas** as linhas, sem exceção: a hierarquia vem do tamanho do
 * número, não de encolher o alvo de toque de ninguém.
 */
export function GroupScorecard({
  groupRoundId,
  header,
  onReview,
  initialHole = 1,
}: {
  groupRoundId: string;
  header: (right: React.ReactNode) => React.ReactNode;
  onReview: () => void;
  /**
   * Onde o cartão abre. Quem entra no meio da volta e diz que **não** jogou os buracos anteriores
   * cai direto no buraco corrente do flight — abrir no 1 mandaria essa pessoa marcar um buraco
   * que ela não jogou, que é o oposto do que ela acabou de responder (canvas 2a).
   */
  initialHole?: number;
}) {
  const group = useGroupRound(groupRoundId);
  const toast = useToast();
  const queryClient = useQueryClient();

  const [holeNumber, setHoleNumber] = useState(initialHole);
  const [draft, setDraft] = useState<Record<string, number>>({});
  /**
   * Qual linha está com os detalhes abertos.
   *
   * Era um booleano quando só a sua linha os tinha. Agora o marcador abre a de qualquer jogador —
   * *"toque num jogador para abrir putts, penalty e bunker"* (canvas 2b) —, e uma por vez, porque
   * quatro conjuntos abertos não cabem numa tela sem rolagem.
   */
  const [openDetailsFor, setOpenDetailsFor] = useState<string | null>(null);
  /** Putts, fairway, penalty e bunker **por participante** — de todos que você pode marcar. */
  const [details, setDetails] = useState<Record<string, OwnDetails>>({});
  /** Scramble: uma tacada por dupla, e os putts também são do time. */
  const [teamDraft, setTeamDraft] = useState<Record<string, number>>({});
  const [teamPutts, setTeamPutts] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  /*
   * A grade é o padrão do desktop, mas não é uma prisão: "um buraco por vez" continua
   * disponível, porque marcar sentado à mesa e marcar no campo são tarefas diferentes e o
   * mesmo jogador faz as duas.
   */
  const isDesktop = useIsDesktop();
  const [gridMode, setGridMode] = useState(true);
  const topRef = useRef<HTMLDivElement>(null);

  const data = group.data;
  const hole = data?.holes.find((h) => h.holeNumber === holeNumber) ?? null;

  /*
   * Ao trocar de buraco, o rascunho parte do que já está gravado — ou do par, que é o palpite
   * honesto de um cartão em branco.
   *
   * O `seededFor` é o que torna a recarga periódica inofensiva. Sem ele, cada chegada de dados
   * novos — inclusive a de um jogador marcando outro buraco — refaria o rascunho por baixo de
   * quem está no meio de uma entrada, apagaria os detalhes abertos e ainda rolaria a tela para o
   * topo. O que o outro jogador marcou continua aparecendo: as linhas que você **não** edita são
   * lidas direto dos dados, e é só o rascunho do editor que fica preservado.
   */
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!data || !hole) return;
    const seedKey = `${data.id}:${holeNumber}`;
    if (seededFor.current === seedKey) return;
    seededFor.current = seedKey;
    const next: Record<string, number> = {};
    for (const p of data.participants) {
      next[p.id] = scoreOf(data, p, holeNumber) ?? hole.par;
    }
    setDraft(next);
    const nextTeams: Record<string, number> = {};
    const nextPutts: Record<string, number> = {};
    for (const team of teamsOf(data)) {
      const entry = data.teamScores[team].find((t) => t.holeNumber === holeNumber);
      nextTeams[team] = entry?.strokes ?? hole.par;
      nextPutts[team] = entry?.putts ?? 0;
    }
    setTeamDraft(nextTeams);
    setTeamPutts(nextPutts);
    /*
     * Os detalhes partem do que já está gravado — o marcador precisa ver o que o jogador marcou
     * por si antes de corrigir. Onde não há nada gravado ficam vazios: putts do buraco anterior
     * herdados em silêncio seriam estatística inventada, e é ela que alimenta GIR e fairway.
     */
    const nextDetails: Record<string, OwnDetails> = {};
    for (const p of data.participants) {
      const saved = holeOf(data, p, holeNumber);
      nextDetails[p.id] = saved
        ? {
            putts: saved.putts ?? EMPTY_DETAILS.putts,
            penaltyStrokes: saved.penaltyStrokes ?? EMPTY_DETAILS.penaltyStrokes,
            bunkerShots: saved.bunkerShots ?? EMPTY_DETAILS.bunkerShots,
            fairwayHit: saved.fairwayHit,
          }
        : { ...EMPTY_DETAILS };
    }
    setDetails(nextDetails);
    setOpenDetailsFor(null);
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [data, hole, holeNumber]);

  const owner = data?.participants.find((p) => p.kind === 'owner') ?? null;

  /** Quem você pode marcar — a regra de "quem edita quem" do canvas (2b). */
  const scorable = data ? scorableParticipants(data) : [];

  /** A linha cujos detalhes estão abertos, e o atalho para editá-los. */
  const openParticipant = openDetailsFor
    ? (data?.participants.find((p) => p.id === openDetailsFor) ?? null)
    : null;
  const openDetails = openDetailsFor ? (details[openDetailsFor] ?? EMPTY_DETAILS) : EMPTY_DETAILS;
  const patchDetail = (patch: Partial<OwnDetails>) => {
    if (!openDetailsFor) return;
    setDetails((prev) => ({
      ...prev,
      [openDetailsFor]: { ...(prev[openDetailsFor] ?? EMPTY_DETAILS), ...patch },
    }));
  };

  const save = async () => {
    if (!data || !owner || !hole) return;
    setSaving(true);
    try {
      const mine = scorableParticipants(data);
      await groupRoundsApi.recordHole(
        groupRoundId,
        holeNumber,
        isScramble(data)
          ? {
              // Uma bola por dupla: não há linha individual para mandar, nem a sua.
              entries: [],
              teamEntries: teamsOf(data).map((team) => ({
                team,
                strokes: teamDraft[team] ?? hole.par,
                putts: teamPutts[team] ?? null,
              })),
            }
          : {
              /*
               * Uma entrada por jogador que você pode marcar — a sua inclusive. O `own` separado
               * existia porque só o dono tinha detalhes; agora que todos têm, mantê-lo seria uma
               * segunda forma de dizer a mesma coisa.
               */
              entries: mine.map((p) => {
                const d = details[p.id] ?? EMPTY_DETAILS;
                return {
                  participantId: p.id,
                  strokes: draft[p.id] ?? hole.par,
                  putts: d.putts,
                  penaltyStrokes: d.penaltyStrokes,
                  bunkerShots: d.bunkerShots,
                  // Par 3 não tem fairway; o servidor força nulo, mas mandar `false` seria dizer
                  // que errou algo que não existe.
                  ...(hole.par > 3 && d.fairwayHit !== null ? { fairwayHit: d.fairwayHit } : {}),
                };
              }),
            },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.groupRound(groupRoundId) }),
        queryClient.invalidateQueries({ queryKey: qk.round(data.roundId) }),
      ]);
      setSavedAt(Date.now());
      if (holeNumber < data.holesPlayed) setHoleNumber((n) => n + 1);
      else onReview();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('record.group.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * O que a grade grava. Ela não conhece a forma da requisição — passa buraco, valores por
   * linha e os putts —, e é aqui que isso vira a chamada que o servidor espera. Uma volta
   * por buraco, como no celular: `recordHole` marca o flight inteiro de uma vez.
   */
  /**
   * A grade grava um buraco inteiro de uma vez, já no formato do `PATCH`.
   *
   * Ela monta as entradas porque é ela que sabe o que está em edição — inclusive putts, fairway,
   * penalidade e bunker de cada jogador, que no 2c ganharam linha própria. Aqui só resta despachar
   * e revalidar; qualquer regra a mais seria uma segunda versão da que a grade já aplicou.
   */
  const saveFromGrid = async (
    holeNumber: number,
    entries: GridHoleEntry[],
    teamEntries: GridTeamEntry[],
  ) => {
    if (!data) return;

    // Nada preenchido neste buraco ainda: gravar seria mandar um buraco vazio.
    if (entries.length === 0 && teamEntries.length === 0) return;

    setSaving(true);
    try {
      await groupRoundsApi.recordHole(groupRoundId, holeNumber, { entries, teamEntries });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.groupRound(groupRoundId) }),
        queryClient.invalidateQueries({ queryKey: qk.round(data.roundId) }),
      ]);
      setSavedAt(Date.now());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('record.group.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (group.isLoading || !data || !hole) {
    return (
      <>
        {header(null)}
        <div className={styles.body}>
          <Skeleton height={120} radius={14} />
        </div>
      </>
    );
  }

  const scramble = isScramble(data);
  const teams = teamsOf(data);
  const yourTeam = data.participants.find((p) => p.kind === 'owner')?.team ?? null;
  const yourStanding = scramble
    ? data.teams.find((t) => t.team === yourTeam)
    : data.standings.find((s) => s.kind === 'owner');
  const scoredHoles = new Set(
    scramble
      ? teams.flatMap((t) => data.teamScores[t].map((s) => s.holeNumber))
      : data.participants.flatMap((p) =>
          p.kind === 'owner' ? viewerScoredHoles(data) : p.scores.map((s) => s.holeNumber),
        ),
  );

  return (
    <>
      {header(
        savedAt ? (
          <span className={styles.savedTag} aria-live="polite">
            {t('record.saved')}
          </span>
        ) : null,
      )}

      {isDesktop && gridMode ? (
        <GroupScoreGrid
          group={data}
          saving={saving}
          onCommit={(holeNumber, entries, teamEntries) =>
            void saveFromGrid(holeNumber, entries, teamEntries)
          }
          onExit={() => setGridMode(false)}
          onReview={onReview}
        />
      ) : (
      <>
      <div ref={topRef} className={styles.holeHead}>
        <div>
          <div className={styles.holeTitle}>{t('hole.number', { number: holeNumber })}</div>
          <div className={styles.holeFacts}>
            {t('record.group.holeFacts', { par: hole.par, si: hole.strokeIndex })}
            {data.format.contest === 'teams' &&
              t('record.group.formatSuffix', {
                format:
                  data.format.teamFormat === 'scramble'
                    ? t('record.scramble')
                    : t('record.bestBall'),
              })}
          </div>
        </div>
        {yourStanding && (
          <div className={styles.holeTotal}>
            <div className={styles.holeTotalValue}>{yourStanding.gross}</div>
            <div className={styles.holeTotalLabel}>
              {scramble
                ? t('record.group.teamTotal', { team: yourTeam ?? '' })
                : t('record.group.yourTotal')}{' '}
              {fmtToPar(yourStanding.toPar)}
            </div>
          </div>
        )}
      </div>

      <div className={styles.flightHead}>
        <span>
          {scramble
            ? t('record.group.oneBallPerPair')
            : data.format.teamFormat === 'best_ball'
              ? t('record.group.ownBall')
              : t('record.group.strokesHere')}
        </span>
        <span>{data.format.handicapMode === 'net' ? t('record.net') : t('record.group.gross')}</span>
      </div>

      {/*
        Scramble: **duas linhas**, uma por dupla. É a consequência estrutural do formato — a dupla
        joga uma bola só, então não há quatro scores para registrar (canvas 1r/1s).
      */}
      {scramble ? (
        <div className={styles.flightRows}>
          {teams.map((team) => {
            const standing = data.teams.find((t) => t.team === team);
            const members = data.participants.filter((p) => p.team === team);
            const strokes = teamDraft[team] ?? hole.par;
            const received =
              data.format.handicapMode === 'net'
                ? strokesReceivedOnHole(
                    standing?.combinedCourseHandicap ?? null,
                    hole.strokeIndex,
                    data.holesPlayed,
                  )
                : 0;
            const isYours = team === yourTeam;
            return (
              <div
                key={team}
                className={cx(styles.flightRow, isYours && styles.flightRowYou)}
              >
                <span className={styles.teamBadge}>{team}</span>
                <div className={styles.flightWho}>
                  <div className={cx(styles.flightName, isYours && styles.flightNameYou)}>
                    {members
                      .map((m) => (m.kind === 'owner' ? t('record.you') : firstNameOf(m.displayName)))
                      .join(' + ')}
                  </div>
                  <div className={styles.flightPoints}>
                    {received > 0 && `${t('record.group.receives', { count: received })} · `}
                    {t('record.group.hcp', {
                      value: fmtDecimal(standing?.combinedCourseHandicap ?? null),
                    })}
                  </div>
                </div>
                <Stepper
                  value={strokes}
                  min={1}
                  max={20}
                  label={t('record.group.teamStrokesAria', { team, number: holeNumber })}
                  onChange={(v) => setTeamDraft((prev) => ({ ...prev, [team]: v }))}
                />
                <div className={cx(styles.flightNet, isYours && styles.flightNetYou)}>
                  {data.format.handicapMode === 'net' ? strokes - received : strokes}
                </div>
              </div>
            );
          })}

          {/* "Putts do time" — no scramble não há a quem atribuí-los. */}
          <div className={styles.detailField} style={{ padding: 'var(--space-3) var(--gutter)' }}>
            <span className={styles.detailLabel}>{t('record.group.teamPutts')}</span>
            <Stepper
              value={teamPutts[yourTeam ?? 'A'] ?? 0}
              min={0}
              max={10}
              size="sm"
              label={t('record.group.teamPuttsAria')}
              onChange={(v) => setTeamPutts((prev) => ({ ...prev, [yourTeam ?? 'A']: v }))}
            />
          </div>
        </div>
      ) : (
      <div className={styles.flightRows}>
        {(teams.length ? teams : [null]).map((team) => {
          const rows = team ? data.participants.filter((p) => p.team === team) : data.participants;
          const bestNet = team ? bestNetOf(rows, data, hole) : null;
          return (
            <div key={team ?? 'all'}>
              {/*
                Best ball: "as mesmas quatro linhas ficam agrupadas sob cabeçalhos de dupla, cada
                um com o melhor net dos dois — o número que interessa ao jogador".
              */}
              {team && (
                <div
                  className={cx(styles.teamRowHead, team === yourTeam && styles.teamRowHeadYou)}
                >
                  <span className={styles.teamBadge}>{team}</span>
                  <span className={styles.teamRowNames}>
                    {rows
                      .map((m) => (m.kind === 'owner' ? t('record.you') : firstNameOf(m.displayName)))
                      .join(' + ')}
                  </span>
                  <span className={styles.teamRowBest}>
                    <span className={styles.teamRowBestLabel}>
                      {t('record.group.bestOf', {
                        mode:
                          data.format.handicapMode === 'net'
                            ? t('record.group.netLower')
                            : t('record.group.grossLower'),
                      })}
                    </span>
                    <span className={styles.teamRowBestValue}>{bestNet ?? '—'}</span>
                  </span>
                </div>
              )}
              {rows.map((p) => {
                /*
                 * "Você" deixou de ser sinônimo de dono: numa volta colaborativa quem está
                 * olhando pode ser o amigo que entrou. A faixa dourada marca a linha de quem
                 * está lendo, seja ele quem for.
                 */
                const isYou = p.id === data.viewer.participantId;
                const canScore = scorable.some((x) => x.id === p.id);
                const strokes = draft[p.id] ?? hole.par;
                const received =
                  data.format.handicapMode === 'net'
                    ? strokesReceivedOnHole(p.courseHandicap, hole.strokeIndex, data.holesPlayed)
                    : 0;
                return (
            <div key={p.id} className={cx(styles.flightRow, isYou && styles.flightRowYou)}>
              <Avatar name={p.displayName} size={30} variant={isYou ? 'gold' : 'green'} />
              <button
                type="button"
                className={styles.flightWho}
                disabled={!canScore || scramble}
                aria-expanded={openDetailsFor === p.id}
                onClick={() => setOpenDetailsFor((cur) => (cur === p.id ? null : p.id))}
                aria-label={t('record.group.openDetails', { name: p.displayName })}
              >
                <div className={cx(styles.flightName, isYou && styles.flightNameYou)}>
                  {isYou ? t('record.you') : p.displayName}
                </div>
                {/*
                  Pontos de handicap em `green-mid`, não em dourado: o dourado é reservado ao
                  acento da tela e ao marcador de "você".
                */}
                {received > 0 && (
                  <div className={styles.flightPoints}>
                    {t('record.group.receives', { count: received })}
                  </div>
                )}
              </button>
              {/*
                Só tem stepper a linha que você pode marcar. As outras mostram o número em texto —
                é o par visual que o canvas (2b) usa para dizer de quem é a responsabilidade, e
                impede que alguém toque num controle que a gravação recusaria.
              */}
              {canScore ? (
                <Stepper
                  value={strokes}
                  min={1}
                  max={20}
                  label={t('record.group.playerStrokesAria', {
                    name: isYou ? t('record.youLower') : p.displayName,
                    number: holeNumber,
                  })}
                  onChange={(v) => setDraft((prev) => ({ ...prev, [p.id]: v }))}
                />
              ) : (
                <div className={styles.flightReadOnly}>{scoreOf(data, p, holeNumber) ?? '—'}</div>
              )}
              <div className={cx(styles.flightNet, isYou && styles.flightNetYou)}>
                {data.format.handicapMode === 'net' && p.courseHandicap !== null
                  ? strokes - received
                  : strokes}
              </div>
            </div>
                );
              })}
            </div>
          );
        })}
      </div>
      )}

      {/*
        Os detalhes de **um** jogador por vez — o que o canvas (2b) resume em "toque num jogador
        para abrir putts, penalty e bunker". Eram só os seus enquanto só o dono tinha cartão;
        agora o marcador abre a linha de qualquer um, e quem entrou abre a própria.
        Um conjunto por vez porque quatro abertos não cabem numa tela sem rolagem.
      */}
      {!scramble && openParticipant && (
      <div className={styles.details}>
        <button
          type="button"
          className={styles.detailsToggle}
          onClick={() => setOpenDetailsFor(null)}
          aria-expanded
        >
          {t('record.group.detailsOf', { name: openParticipant.displayName })}
          <span aria-hidden="true">{t('record.group.collapse')}</span>
        </button>

        {true && (
          <div className={styles.detailsBody}>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>{t('round.putts')}</span>
              <Stepper
                value={openDetails.putts}
                min={0}
                max={10}
                size="sm"
                label={t('record.group.puttsAria')}
                onChange={(v) => patchDetail({ putts: v })}
              />
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>{t('round.penalty')}</span>
              <Stepper
                value={openDetails.penaltyStrokes}
                min={0}
                max={10}
                size="sm"
                label={t('record.group.penaltyAria')}
                onChange={(v) => patchDetail({ penaltyStrokes: v })}
              />
            </div>
            <div className={styles.detailField}>
              <span className={styles.detailLabel}>{t('round.bunker')}</span>
              <Stepper
                value={openDetails.bunkerShots}
                min={0}
                max={10}
                size="sm"
                label={t('record.group.bunkerAria')}
                onChange={(v) => patchDetail({ bunkerShots: v })}
              />
            </div>
            {/* Par 3 não tem fairway a acertar — a linha some, em vez de oferecer uma escolha falsa. */}
            {hole.par > 3 && (
              <div className={styles.detailField}>
                <span className={styles.detailLabel}>{t('round.fairway')}</span>
                <span className={styles.pills}>
                  <Pill
                    selected={openDetails.fairwayHit === false}
                    onClick={() => patchDetail({ fairwayHit: false })}
                  >
                    {t('record.missed')}
                  </Pill>
                  <Pill
                    selected={openDetails.fairwayHit === true}
                    onClick={() => patchDetail({ fairwayHit: true })}
                  >
                    {t('record.hit')}
                  </Pill>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      <div className={styles.holeStrip} role="tablist" aria-label={t('round.holes')}>
        {Array.from({ length: data.holesPlayed }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            role="tab"
            aria-selected={n === holeNumber}
            className={cx(
              styles.holeChip,
              n === holeNumber && styles.holeChipActive,
              scoredHoles.has(n) && styles.holeChipDone,
            )}
            onClick={() => setHoleNumber(n)}
          >
            {n}
          </button>
        ))}
      </div>

      <div className={styles.flightActions}>
        <Button
          variant="secondary"
          disabled={holeNumber === 1}
          onClick={() => setHoleNumber((n) => Math.max(1, n - 1))}
        >
          {t('record.previous')}
        </Button>
        <Button block busy={saving} onClick={() => void save()}>
          {holeNumber < data.holesPlayed
            ? t('hole.number', { number: holeNumber + 1 })
            : t('record.review')}
        </Button>
      </div>
      {isDesktop && (
        <div className={styles.gridSwitch}>
          <Button variant="secondary" size="sm" onClick={() => setGridMode(true)}>
            {t('record.group.seeWholeCard')}
          </Button>
        </div>
      )}
      </>
      )}
    </>
  );
}

interface OwnDetails {
  putts: number;
  penaltyStrokes: number;
  bunkerShots: number;
  fairwayHit: boolean | null;
}

const EMPTY_DETAILS: OwnDetails = {
  putts: 0,
  penaltyStrokes: 0,
  bunkerShots: 0,
  fairwayHit: null,
};

/**
 * O buraco de um jogador, venha de onde vier.
 *
 * O servidor já achatou as duas fontes em `scoresByParticipant`: quem entrou na volta guarda na
 * própria `Round`, o convidado sem conta guarda no participante. A tela não precisa saber de qual
 * — e é justamente por não precisar que ela funciona igual para o dono, para o amigo que entrou e
 * para quem não tem conta.
 */

function scoreOf(
  group: GroupRound,
  participant: GroupParticipant,
  holeNumber: number,
): number | null {
  return holeOf(group, participant, holeNumber)?.strokes ?? null;
}

/** O melhor net (ou bruto) da dupla neste buraco — o número que o cabeçalho do time mostra. */
function bestNetOf(
  rows: GroupParticipant[],
  group: GroupRound,
  hole: { strokeIndex: number; holeNumber: number },
): number | null {
  const values = rows
    .map((p) => {
      const score = holeOf(group, p, hole.holeNumber);
      if (!score) return null;
      const received =
        group.format.handicapMode === 'net'
          ? strokesReceivedOnHole(p.courseHandicap, hole.strokeIndex, group.holesPlayed)
          : 0;
      return score.strokes - received;
    })
    .filter((v): v is number => v !== null);
  return values.length === 0 ? null : Math.min(...values);
}

/**
 * Quem **você** pode marcar neste flight — a regra que o canvas (2b) chama de "quem edita quem".
 *
 * O dono é o marcador do flight, papel que existe no golfe: edita todas as linhas. Quem entrou
 * edita só a própria. A mesma regra roda no servidor, que é quem de fato decide; aqui ela serve
 * para a tela não oferecer um stepper que a gravação recusaria.
 */

function firstNameOf(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName;
}

function isScramble(group: GroupRound): boolean {
  return group.format.contest === 'teams' && group.format.teamFormat === 'scramble';
}

function teamsOf(group: GroupRound): TeamId[] {
  const used = new Set(
    group.participants.map((p) => p.team).filter((t): t is TeamId => t !== null),
  );
  return (['A', 'B'] as TeamId[]).filter((t) => used.has(t));
}

/**
 * Os buracos que **você** já marcou — os que ficam preenchidos na tira de navegação.
 *
 * Era do dono enquanto só ele tinha cartão. Agora é de quem está olhando: o amigo que entrou tem
 * o próprio progresso, e mostrar o do dono no lugar dele diria que buracos que ele não jogou já
 * estão feitos.
 */
function viewerScoredHoles(group: GroupRound): number[] {
  const id = group.viewer.participantId;
  if (!id) return [];
  return (group.scoresByParticipant[id] ?? []).map((s) => s.holeNumber);
}
